import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { LoginPage } from '../features/auth/LoginPage';
import { LogoutPage } from '../features/auth/LogoutPage';
import { PageFallback } from './PageFallback';
import { RequireAdmin } from './RequireAdmin';
import { RequireAuth } from './RequireAuth';

const ProjectsPage = lazy(() => import('../features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const ProjectDetailPage = lazy(() =>
  import('../features/project-detail/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })),
);
const ActivitiesPage = lazy(() => import('../features/activities/ActivitiesPage').then((m) => ({ default: m.ActivitiesPage })));
const LogsPage = lazy(() => import('../features/logs/LogsPage').then((m) => ({ default: m.LogsPage })));
const UsersPage = lazy(() => import('../features/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const ApiKeysPage = lazy(() => import('../features/api-keys/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })));
const ApiDocsPage = lazy(() => import('../features/api-docs/ApiDocsPage').then((m) => ({ default: m.ApiDocsPage })));

function withSuspense(element: JSX.Element) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

/**
 * The route tree. `/login` is public; every other route is wrapped in
 * RequireAuth, which redirects to /login whenever there's no authenticated
 * session (see RequireAuth.tsx and store/auth.store.ts).
 */
export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/logout', element: <LogoutPage /> },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <Navigate to="/projects" replace /> },
      { path: '/projects', element: withSuspense(<ProjectsPage />) },
      { path: '/projects/:code', element: withSuspense(<ProjectDetailPage />) },
      { path: '/activities', element: withSuspense(<ActivitiesPage />) },
      { path: '/logs', element: withSuspense(<LogsPage />) },
      { path: '/api-docs', element: withSuspense(<ApiDocsPage />) },
      { path: '/api-keys', element: <RequireAdmin>{withSuspense(<ApiKeysPage />)}</RequireAdmin> },
      { path: '/users', element: <RequireAdmin>{withSuspense(<UsersPage />)}</RequireAdmin> },
    ],
  },
  { path: '*', element: <Navigate to="/projects" replace /> },
];

export const router = createBrowserRouter(routes);
